import React from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { FORM_MODE } from '@awp/constants';
import * as Actions from './Actions';
/**
 * A component that renders buttons for manipulating data graphs within a form.
 *
 * @param {string} sectionId - The unique ID of the data graph section.
 * @param {Object} presetData - Data related to the current preset.
 * @param {Object} formik - The Formik context object for form management.
 * @returns {React.Component} The rendered GraphButtons component.
 */
export const GraphButtons = (sectionId, presetData, formik) => {
  const displayOrder = parseInt(sectionId.match(/_(\d+)/)[1], 10);
  const dispatch = useDispatch();

  // Functionality:
  // This component renders buttons for adding, moving, and deleting data graphs within a form.
  // Each button corresponds to a specific action, such as adding a new graph or deleting an existing one.
  // When a button is clicked, it dispatches the corresponding action to update the state.

  return (
    <div>
      {/* Add Graph Button */}
      <Button
        className="fas fa-plus awp-img-btn"
        title="Add Graph"
        variant="light"
        onClick={(e) => {
          e.stopPropagation();
          dispatch(Actions.addNewDataGraph(presetData.GraphPresetId, formik));
          // Dispatches an action to add a new data graph to the form.
          // Parameters: graphPresetId - ID of the current graph preset, formik - Formik context.
        }}
      />
      {/* Move Graph Up Button */}
      <Button
        className="fas fa-arrow-up awp-img-btn"
        title="Move Graph Up"
        variant="light"
        disabled={displayOrder === 1}
        onClick={(e) => {
          e.stopPropagation();
          dispatch(Actions.moveDataGraph(presetData.GraphPresetId, displayOrder, 'up', formik));
          // Dispatches an action to move the data graph up in the form.
          // Parameters: graphPresetId - ID of the current graph preset,
          // displayOrder - current display order of the data graph,
          // 'up' - direction of movement (upward), formik - Formik context.
        }}
      />
      {/* Move Graph Down Button */}
      <Button
        className="fas fa-arrow-down awp-img-btn"
        title="Move Graph Down"
        disabled={presetData.DataGraphs.length === displayOrder}
        variant="light"
        onClick={(e) => {
          e.stopPropagation();
          dispatch(Actions.moveDataGraph(presetData.GraphPresetId, displayOrder, 'down', formik));
          // Dispatches an action to move the data graph down in the form.
          // Parameters: graphPresetId - ID of the current graph preset,
          // displayOrder - current display order of the data graph,
          // 'down' - direction of movement (downward), formik - Formik context.
        }}
      />
      {/* Delete Graph Button */}
      <Button
        className="fas fa-trash awp-img-btn"
        title="Delete Graph"
        variant="light"
        disabled={presetData.DataGraphs.length === 1}
        onClick={(e) => {
          e.stopPropagation();
          dispatch(Actions.removeDataGraph(presetData.GraphPresetId, displayOrder, formik));
          // Dispatches an action to remove the data graph from the form.
          // Parameters: graphPresetId - ID of the current graph preset,
          // displayOrder - current display order of the data graph, formik - Formik context.
        }}
      />
    </div>
  );
};

/**
 * Renders buttons for adding and deleting weather stations.
 *
 * @param {number} displayOrder - The display order of the weather station group.
 * @param {Object} presetData - The data associated with the current graph preset.
 * @param {number} stationId - The ID of the weather station.
 * @param {Function} setActiveKey - A function to set the active key for the Accordion.
 * @param {Object} formik - The Formik context object for form management.
 * @returns {React.Component} Rendered weather station buttons component.
 */
export const stationButtons = (displayOrder, presetData, stationId, setActiveKey, formik) => {
  const dispatch = useDispatch();

  // Functionality:
  // This component renders buttons for adding and deleting weather stations.
  // Each button corresponds to a specific action, such as adding a new station or deleting an existing one.
  // When a button is clicked, it dispatches the corresponding action to update the state.

  return (
    <div>
      {/* Add Weather Station Button */}
      <Button
        className="fas fa-plus awp-img-btn"
        title="Add Weather Station"
        variant="light"
        onClick={(e) => {
          e.stopPropagation();
          dispatch(Actions.addNewGraphFilter(presetData.DataGraphPresetId, displayOrder, formik));
        }}
      />
      {/* Delete Weather Station Button */}
      <Button
        className="fas fa-trash awp-img-btn"
        title="Delete Weather Station"
        variant="light"
        disabled={false}
        onClick={(e) => {
          e.stopPropagation();
          dispatch(Actions.removeGraphFilter(presetData.DataGraphPresetId, stationId, displayOrder, formik));
          setActiveKey(null);
        }}
      />
    </div>
  );
};

/**
 * A component that renders buttons for manipulating graph filters.
 *
 * @param {number} displayOrder - The display order of the graph filter.
 * @param {number} graphPresetId - The ID of the graph preset.
 * @param {number} weatherStationId - The ID of the weather station.
 * @param {number} typeId - The ID of the observation type.
 * @param {Object} formik - The Formik context object for form management.
 * @returns {React.Component} The rendered GraphFilterButtons component.
 */
export const GraphFilterButtons = ({
  displayOrder,
  graphPresetId,
  weatherStationId,
  placementName,
  typeId,
  formik,
}) => {
  const dispatch = useDispatch();

  // Functionality:
  // This component renders buttons for adding and deleting observation filters within a graph.
  // Each button corresponds to a specific action, such as adding a new observation or deleting an existing one.
  // When a button is clicked, it dispatches the corresponding action to update the state.

  return (
    <Form.Group className="my-1">
      <InputGroup className="mb-3 mt-2 preset-obs-button-grp">
        {/* Add Observation Button */}
        <Button
          className="fas fa-plus awp-img-btn"
          title="Add Observation"
          variant="outline-light"
          style={{
            color: '#000',
            borderColor: 'transparent',
            backgroundColor: 'transparent',
          }}
          onClick={(e) => {
            e.stopPropagation();
            dispatch(Actions.addDataset(graphPresetId, weatherStationId, displayOrder, formik));
          }}
        />
        {/* Delete Observation Button */}
        <Button
          className="fas fa-trash awp-img-btn"
          title="Delete Observation"
          variant="outline-light"
          style={{
            color: '#000',
            borderColor: 'transparent',
            backgroundColor: 'transparent',
          }}
          disabled={false}
          onClick={(e) => {
            e.stopPropagation();
            dispatch(
              Actions.removeDataset(graphPresetId, weatherStationId, displayOrder, placementName, typeId, formik),
            );
          }}
        />
      </InputGroup>
    </Form.Group>
  );
};
